import React from 'react';
import { Droplet, Flame, Trash2, Home, ArrowUp, ArrowDown, TrendingUp, Equal, Brush, AlertCircle } from 'lucide-react';
import { Municipality, getFeesForYear, filterFees, getYearlyTotal } from '@/lib/data';
import { Progress } from '@/components/ui/progress';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { useFeeCategories } from '@/contexts/FeeCategoryContext';

interface FeeBreakdownProps {
  municipality: Municipality;
  selectedYear?: number;
}

const FeeBreakdown: React.FC<FeeBreakdownProps> = ({ municipality, selectedYear }) => {
  const { enabledCategories } = useFeeCategories();
  const years = municipality.historicalData.map(data => data.year);
  const latestYear = Math.max(...years);
  const earliestYear = Math.min(...years);
  const displayYear = selectedYear ?? latestYear;

  const allFeesToShow = selectedYear
    ? getFeesForYear(municipality, selectedYear)
    : municipality.currentFees;
  const feesToShow = filterFees(allFeesToShow, enabledCategories);
  const totalFees = feesToShow.reduce((sum, fee) => sum + fee.amount, 0);

  const hasEstimatedData = feesToShow.some(fee => fee.isEstimated);
  
  // Beregn endring fra forrige år for hver avgiftstype
  const calculateYearlyChange = (feeId: string) => {
    const histData = municipality.historicalData;
    
    if (!histData || histData.length < 2) return null;
    
    const sortedData = [...histData].sort((a, b) => a.year - b.year);
    const yearIndex = sortedData.findIndex(d => d.year === displayYear);
    if (yearIndex <= 0) return null;
    
    const currentYear = sortedData[yearIndex];
    const previousYear = sortedData[yearIndex - 1];
    
    let currentFee = 0;
    let previousFee = 0;
    
    switch (feeId) {
      case 'water':
        currentFee = currentYear.waterFee;
        previousFee = previousYear.waterFee;
        break;
      case 'sewage':
        currentFee = currentYear.sewageFee;
        previousFee = previousYear.sewageFee;
        break;
      case 'waste':
        currentFee = currentYear.wasteFee;
        previousFee = previousYear.wasteFee;
        break;
      case 'property':
        currentFee = currentYear.propertyTax;
        previousFee = previousYear.propertyTax;
        break;
      case 'chimney':
        currentFee = currentYear.chimneySweepFee;
        previousFee = previousYear.chimneySweepFee;
        break;
    }
    
    if (previousFee <= 0) return null; // Unngå divisjon på 0
    
    const percentChange = ((currentFee - previousFee) / previousFee) * 100;
    return {
      percentChange,
      absoluteChange: currentFee - previousFee,
      previousYear: previousYear.year,
      currentYear: currentYear.year
    };
  };
  
  // Hent feeType-ikon
  const getIconForFee = (feeId: string) => {
    switch (feeId) {
      case 'water':
        return <Droplet className="h-3.5 w-3.5 text-blue-600" />;
      case 'sewage':
        return <Flame className="h-3.5 w-3.5 text-orange-600" />;
      case 'waste':
        return <Trash2 className="h-3.5 w-3.5 text-slate-600" />;
      case 'property':
        return <Home className="h-3.5 w-3.5 text-slate-600" />;
      case 'chimney':
        return <Brush className="h-3.5 w-3.5 text-amber-600" />;
      default:
        return null;
    }
  };
  
  // Hent ikonet for endringsretning
  const getChangeIcon = (change: number | null) => {
    if (change === null) return <Equal className="h-3 w-3 text-gray-400" />;
    if (change > 5) return <ArrowUp className="h-3 w-3 text-red-500" />;
    if (change > 0) return <TrendingUp className="h-3 w-3 text-orange-500" />;
    if (change < 0) return <ArrowDown className="h-3 w-3 text-slate-500" />;
    return <Equal className="h-3 w-3 text-gray-400" />;
  };
  
  const getIconBgClass = (feeId: string) => {
    switch (feeId) {
      case 'water': return 'bg-blue-50';
      case 'sewage': return 'bg-orange-50';
      case 'waste': return 'bg-slate-100';
      case 'property': return 'bg-slate-100';
      case 'chimney': return 'bg-amber-50';
      default: return 'bg-slate-50';
    }
  };

  return (
    <div className="bg-white p-4 md:p-5 rounded-lg shadow-sm border border-slate-100">
      <p className="text-xs font-medium text-warmGray uppercase tracking-wide mb-4">Avgiftsfordeling</p>
      <h3 className="text-lg font-semibold text-deepCharcoal mb-4">{municipality.name}</h3>
      
      <div className="space-y-4">
        {feesToShow.map((fee) => {
          const yearlyChange = calculateYearlyChange(fee.id);
          const isEstimated = fee.isEstimated || false;
          
          return (
            <div key={fee.id} className="fee-item">
              <div className="flex justify-between items-center mb-1">
                <div className="flex items-center gap-2">
                  <div className={`rounded-lg p-1.5 shrink-0 ${getIconBgClass(fee.id)}`}>
                    {getIconForFee(fee.id)}
                  </div>
                  <span className="text-sm font-medium">{fee.name}</span>
                  {isEstimated && (
                    <span className="text-xs text-amber-600 font-medium">(estimert)</span>
                  )}
                </div>
                <div className="flex flex-col items-end">
                  <div className="flex items-center">
                    <span className="text-sm font-semibold">{fee.amount.toLocaleString('nb-NO')} {fee.unit}</span>
                    {isEstimated && (
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger>
                            <AlertCircle className="h-3 w-3 ml-1 text-amber-500" />
                          </TooltipTrigger>
                          <TooltipContent>
                            <p>Estimert fra forrige års data (SSB har ikke publisert dette ennå)</p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    )}
                  </div>
                  {yearlyChange && (
                    <div className="flex items-center text-xs">
                      {getChangeIcon(yearlyChange.percentChange)}
                      <span className={`ml-1 ${
                        yearlyChange.percentChange > 0 
                          ? 'text-red-500' 
                          : yearlyChange.percentChange < 0 
                            ? 'text-slate-500' 
                            : 'text-gray-500'
                      }`}>
                        {yearlyChange.percentChange > 0 ? '+' : ''}
                        {yearlyChange.percentChange.toFixed(1)}% fra {yearlyChange.previousYear}
                      </span>
                    </div>
                  )}
                </div>
              </div>
              <Progress 
                value={(fee.amount / totalFees) * 100} 
                className="h-2" 
              />
              <p className="text-xs text-muted-foreground mt-1">
                {((fee.amount / totalFees) * 100).toFixed(1)}% av totale kommunale avgifter
              </p>
            </div>
          );
        })}
      </div>
      
      <div className="mt-6 pt-4 border-t border-slate-100">
        <div className="flex justify-between">
          <span className="font-medium">Totale årlige avgifter:</span>
          <span className="font-bold text-nordicBlue">{totalFees.toLocaleString('nb-NO')} kr/år</span>
        </div>
        <p className="text-xs text-muted-foreground mt-1">
          Basert på en standardbolig på 120m² i {municipality.name} for {displayYear}
        </p>
        
        {municipality.historicalData.length >= 2 && (() => {
          const oldestTotal = getYearlyTotal(municipality.historicalData[0], enabledCategories);
          const newestTotal = getYearlyTotal(municipality.historicalData[municipality.historicalData.length - 1], enabledCategories);
          return (
          <div className="mt-3 pt-3 border-t border-slate-100">
            <h4 className="text-sm font-medium mb-2">Utvikling over tid</h4>
            <div className="text-xs text-muted-foreground">
              <p>
                Siden {earliestYear} har de totale avgiftene i {municipality.name}
                {oldestTotal < newestTotal
                  ? ` økt med ${(((newestTotal - oldestTotal) / oldestTotal) * 100).toFixed(1)}% `
                  : ` sunket med ${(((oldestTotal - newestTotal) / oldestTotal) * 100).toFixed(1)}% `
                }
                (fra {oldestTotal.toLocaleString('nb-NO')} kr til {newestTotal.toLocaleString('nb-NO')} kr).
              </p>
              <p className="mt-2 text-xs text-gray-400">
                Data fra SSB tabell 12842: Kommunale gebyrer knyttet til bolig ({earliestYear}-{latestYear})
                {hasEstimatedData && <span className="ml-1 italic">(markert med «estimert» per kategori)</span>}
              </p>
            </div>
          </div>
          );
        })()}
      </div>
    </div>
  );
};

export default FeeBreakdown;
